import { gql, useMutation, useQuery, useSubscription } from "@apollo/client";
import { Close, KeyboardArrowDown } from "@mui/icons-material";
import {
  Card,
  CircularProgress,
  Fade,
  Icon,
  IconButton,
  InputLabel,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { MenuContext } from "context/menuContext";
import { UserContext } from "context/user";
import PropTypes from "prop-types";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { printComanda } from "utils/comanda";
import {
  detalleEsImprimible,
  detallePerteneceASeccion,
  detalleRequiereImpresionComanda,
  formatearResumenImpresionesFallidas,
  obtenerSeccionesInvolucradas,
  resumirImpresionesFallidasPorComandera,
} from "utils/seccionesComanda";
import AgregarProducto from "./components/AgregarProducto";
import ListadoDetalles from "./components/ListadoDetalles.jsx";
import ModalFacturar from "./components/ModalFacturar";
import { ModalPedidoProvider } from "./context/ModalPedidoContext";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "100%", sm: "95%", xxl: "50%" },
  overflowY: "auto",
  p: 3,
};

const PEDIDO_DRAFT_STORAGE_PREFIX = "modal-pedido-areas-draft";
const PEDIDO_DRAFT_STORAGE_VERSION = 1;
const PEDIDO_DRAFT_PERSIST_DEBOUNCE_MS = 180;
const PEDIDO_OUTBOX_SYNC_DEBOUNCE_MS = 650;

const SAVE_PEDIDO_MUTATION = gql`
  mutation modalSavePedido($input: PedidoInput!) {
    savePedido(input: $input) {
      id
      idMesa
      idMozo
      estado
      paraLlevar
      comentario
      mozo {
        id
        nombre
        apellido
      }
      detalles {
        id
        clientDetailKey
        estado
        cantidad
        precio
        esElaborado
        numeroComanda
        horarioEntrega
        comentario
        motivoCancelacion
        impresionComanda
        producto {
          id
          nombre
          seccion
          combo
          esElaborado
          precioCarta
          precioOnline
          precioPorMayor
          precioPorMayorOnline
          cantidadPorMayor
          comanderas {
            id
            printName
            habilitado
            estadoConexion
          }
        }
        ingredientesQuitados {
          id
        }
      }
      updatedAt
    }
  }
`;

const SYNC_DETALLE_PEDIDO_MESA_MUTATION = gql`
  mutation syncDetallePedidoMesa($input: PedidoMesaDetalleSyncInput!) {
    syncDetallePedidoMesa(input: $input) {
      id
      idMesa
      idMozo
      estado
      paraLlevar
      comentario
      mozo {
        id
        nombre
        apellido
      }
      detalles {
        id
        clientDetailKey
        estado
        cantidad
        precio
        esElaborado
        numeroComanda
        horarioEntrega
        comentario
        motivoCancelacion
        impresionComanda
        producto {
          id
          nombre
          seccion
          combo
          esElaborado
          precioCarta
          precioOnline
          precioPorMayor
          precioPorMayorOnline
          cantidadPorMayor
          comanderas {
            id
            printName
            habilitado
            estadoConexion
          }
        }
        ingredientesQuitados {
          id
        }
      }
      updatedAt
    }
  }
`;

const DELETE_PEDIDO_MUTATION = gql`
  mutation modalDeletePedido($id: ID!) {
    deletePedido(id: $id) {
      id
    }
  }
`;

const NUEVO_PEDIDO_STOCK_SUBSCRIPTION = gql`
  subscription modalNuevoPedidoStock {
    nuevoPedido {
      id
      idMesa
      updatedAt
    }
  }
`;

const PEDIDO_MODIFICADO_STOCK_SUBSCRIPTION = gql`
  subscription modalPedidoModificadoStock {
    pedidoModificado {
      id
      idMesa
      updatedAt
    }
  }
`;

const PEDIDO_ELIMINADO_STOCK_SUBSCRIPTION = gql`
  subscription modalPedidoEliminadoStock {
    pedidoEliminado {
      id
      idMesa
      updatedAt
    }
  }
`;

const sameIngredientes = (current = [], target = []) =>
  current.length === target.length &&
  current.every((ingrediente) => target.find((ingredienteTarget) => ingredienteTarget.id === ingrediente.id));

const getDetalleIdentity = (detalle = {}) =>
  detalle?.optimisticKey
    ? detalle.optimisticKey
    : detalle?.id
    ? `id:${detalle.id}`
    : `prod:${detalle?.producto?.id || detalle?.idProducto || "na"}:comanda:${detalle?.numeroComanda || "na"}`;

const normalizeDetalle = (detalle = {}) => ({
  ...detalle,
  id: detalle?.id || null,
  cantidad: Number(detalle?.cantidad || 0),
  precio: Number(detalle?.precio || 0),
  estado: detalle?.estado || "PENDIENTE",
  horarioEntrega: detalle?.horarioEntrega || null,
  comentario: detalle?.comentario || "",
  motivoCancelacion: detalle?.motivoCancelacion || null,
  esElaborado: !!(detalle?.esElaborado ?? detalle?.producto?.esElaborado),
  numeroComanda: detalle?.numeroComanda || null,
  impresionComanda: detalle?.impresionComanda || null,
  producto: detalle?.producto
    ? {
        ...detalle.producto,
        id: Number(detalle.producto.id),
      }
    : null,
  ingredientesQuitados: detalle?.ingredientesQuitados || [],
});

const createEmptyPedidoState = (fallbackMozoId = null) => ({
  id: null,
  mozo: fallbackMozoId,
  paraLlevar: false,
  comentario: "",
  detalles: [],
});

const normalizePedidoState = (pedidoSource, fallbackMozoId = null) => {
  if (!pedidoSource) {
    return createEmptyPedidoState(fallbackMozoId);
  }

  const { createdAt, __typename, mozo, detalles, ...restPedido } = pedidoSource;

  return {
    ...restPedido,
    id: restPedido?.id || null,
    mozo: mozo?.id || restPedido?.idMozo || fallbackMozoId || null,
    paraLlevar: !!restPedido?.paraLlevar,
    comentario: restPedido?.comentario || "",
    detalles: (detalles || []).map(normalizeDetalle),
  };
};

const buildPedidoInput = ({ pedidoState, mesaId, idSede, paraFacturar = false, estado = undefined }) => ({
  id: pedidoState?.id || null,
  idMesa: mesaId,
  idMozo: pedidoState?.mozo || null,
  total: null,
  paraFacturar,
  ...(estado ? { estado } : {}),
  paraLlevar: !!pedidoState?.paraLlevar,
  comentario: pedidoState?.comentario || "",
  idSede,
  detalles: (pedidoState?.detalles || []).map((detalle) => ({
    id: detalle?.id || null,
    idPedido: pedidoState?.id || null,
    idProducto: detalle?.producto?.id || detalle?.idProducto,
    clientDetailKey: detalle?.clientDetailKey || detalle?.optimisticKey || null,
    cantidad: Number(detalle?.cantidad || 0),
    precio: Number(detalle?.precio || 0),
    idsIngredientesQuitados: (detalle?.ingredientesQuitados || []).map((ingrediente) => ingrediente.id),
    estado: detalle?.estado,
    horarioEntrega: detalle?.horarioEntrega,
    esElaborado: !!(detalle?.esElaborado || detalle?.producto?.esElaborado),
    motivoCancelacion: detalle?.motivoCancelacion,
    comentario: detalle?.comentario || "",
  })),
});

const buildPedidoStateForDiscard = ({ pedidoOriginalState, pedidoPersistidoState }) => {
  const idsOriginales = new Set(
    (pedidoOriginalState?.detalles || []).map((detalle) => Number(detalle?.id)).filter(Boolean)
  );

  const detallesCreadosEnSesion = (pedidoPersistidoState?.detalles || [])
    .filter((detalle) => Number(detalle?.id) && !idsOriginales.has(Number(detalle.id)))
    .map((detalle) => ({
      ...detalle,
      estado: "CANCELADO",
    }));

  return {
    ...pedidoOriginalState,
    detalles: [...(pedidoOriginalState?.detalles || []), ...detallesCreadosEnSesion],
  };
};

const hasDetalleEntregado = (pedidoState = {}) =>
  (pedidoState?.detalles || []).some((detalle) => detalle?.estado === "ENTREGADO");

const hasDetallesActivos = (pedidoState = {}) =>
  (pedidoState?.detalles || []).some((detalle) => detalle?.estado !== "CANCELADO");

const shouldDeletePedidoOnClose = ({ pedidoState = {}, pedidoPersistidoState = {} }) =>
  !!pedidoPersistidoState?.id && !hasDetalleEntregado(pedidoState) && !hasDetallesActivos(pedidoState);

const hasRealChangesBetweenStates = ({ pedidoOriginalState = {}, pedidoState = {}, pedidoPersistidoState = {} }) => {
  if (!pedidoOriginalState?.id) {
    return (
      !!pedidoPersistidoState?.id ||
      (pedidoState?.detalles || []).length > 0 ||
      !!pedidoState?.comentario ||
      !!pedidoState?.paraLlevar
    );
  }

  const originalMozoId = pedidoOriginalState?.mozo || null;
  const currentMozoId = pedidoState?.mozo || null;
  const originalParaLlevar = !!pedidoOriginalState?.paraLlevar;
  const currentParaLlevar = !!pedidoState?.paraLlevar;
  const originalComentario = pedidoOriginalState?.comentario || "";
  const currentComentario = pedidoState?.comentario || "";

  if (
    originalMozoId !== currentMozoId ||
    originalParaLlevar !== currentParaLlevar ||
    originalComentario !== currentComentario
  ) {
    return true;
  }

  const originalDetalles = pedidoOriginalState?.detalles || [];
  const currentDetalles = pedidoState?.detalles || [];

  if (originalDetalles.length !== currentDetalles.length) {
    return true;
  }

  if (originalDetalles.length === 0 && currentDetalles.length === 0) {
    return false;
  }

  return !originalDetalles.every((originalDetalle, index) => {
    const currentDetalle = currentDetalles[index];
    if (!currentDetalle) return false;

    return (
      originalDetalle.id === currentDetalle.id &&
      originalDetalle.producto?.id === currentDetalle.producto?.id &&
      originalDetalle.cantidad === currentDetalle.cantidad &&
      originalDetalle.precio === currentDetalle.precio &&
      originalDetalle.estado === currentDetalle.estado &&
      (originalDetalle.horarioEntrega || null) === (currentDetalle.horarioEntrega || null) &&
      (originalDetalle.comentario || "") === (currentDetalle.comentario || "") &&
      (originalDetalle.motivoCancelacion || null) === (currentDetalle.motivoCancelacion || null) &&
      sameIngredientes(originalDetalle.ingredientesQuitados || [], currentDetalle.ingredientesQuitados || [])
    );
  });
};

const buildDetalleInputForSync = (detalle = {}, pedidoState = {}) => ({
  id: detalle?.id || null,
  idPedido: pedidoState?.id || null,
  idProducto: detalle?.producto?.id || detalle?.idProducto,
  clientDetailKey: detalle?.clientDetailKey || detalle?.optimisticKey || null,
  cantidad: Number(detalle?.cantidad || 0),
  precio: Number(detalle?.precio || 0),
  idsIngredientesQuitados: (detalle?.ingredientesQuitados || []).map((ingrediente) => ingrediente.id),
  estado: detalle?.estado || "PENDIENTE",
  horarioEntrega: detalle?.horarioEntrega || null,
  esElaborado: !!(detalle?.esElaborado || detalle?.producto?.esElaborado),
  motivoCancelacion: detalle?.motivoCancelacion || null,
  comentario: detalle?.comentario || "",
});

const buildLocalDetalleOverlay = (detalleLocal = {}) => ({
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "cantidad")
    ? { cantidad: Number(detalleLocal?.cantidad || 0) }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "precio")
    ? { precio: Number(detalleLocal?.precio || 0) }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "estado")
    ? { estado: detalleLocal?.estado || "PENDIENTE" }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "horarioEntrega")
    ? { horarioEntrega: detalleLocal?.horarioEntrega || null }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "comentario")
    ? { comentario: detalleLocal?.comentario || "" }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "motivoCancelacion")
    ? { motivoCancelacion: detalleLocal?.motivoCancelacion || null }
    : {}),
  ...(Object.prototype.hasOwnProperty.call(detalleLocal || {}, "ingredientesQuitados")
    ? { ingredientesQuitados: detalleLocal?.ingredientesQuitados || [] }
    : {}),
});

const buildDetalleSyncComparable = (detalle = {}) => ({
  idProducto: Number(detalle?.producto?.id || detalle?.idProducto || 0),
  cantidad: Number(detalle?.cantidad || 0),
  precio: Number(detalle?.precio || 0),
  estado: detalle?.estado || "PENDIENTE",
  horarioEntrega: detalle?.horarioEntrega || null,
  comentario: detalle?.comentario || "",
  motivoCancelacion: detalle?.motivoCancelacion || null,
  esElaborado: !!(detalle?.esElaborado || detalle?.producto?.esElaborado),
  idsIngredientesQuitados: (detalle?.ingredientesQuitados || [])
    .map((ingrediente) => Number(typeof ingrediente === "object" ? ingrediente?.id : ingrediente))
    .filter(Boolean)
    .sort((left, right) => left - right),
});

const areDetalleSyncComparablesEqual = (left = {}, right = {}) => {
  const leftComparable = buildDetalleSyncComparable(left);
  const rightComparable = buildDetalleSyncComparable(right);

  return (
    leftComparable.idProducto === rightComparable.idProducto &&
    leftComparable.cantidad === rightComparable.cantidad &&
    leftComparable.precio === rightComparable.precio &&
    leftComparable.estado === rightComparable.estado &&
    leftComparable.horarioEntrega === rightComparable.horarioEntrega &&
    leftComparable.comentario === rightComparable.comentario &&
    leftComparable.motivoCancelacion === rightComparable.motivoCancelacion &&
    leftComparable.esElaborado === rightComparable.esElaborado &&
    leftComparable.idsIngredientesQuitados.length === rightComparable.idsIngredientesQuitados.length &&
    leftComparable.idsIngredientesQuitados.every(
      (ingredienteId, index) => ingredienteId === rightComparable.idsIngredientesQuitados[index]
    )
  );
};

const getPersistedDetalleBindingIdentity = (detalle = {}) => detalle?.clientDetailKey || getDetalleIdentity(detalle);

const mergeLocalNonPersistedChanges = ({ nextPersisted, prevLocal, prevPersisted }) => {
  const localByIdentity = new Map(
    (prevLocal?.detalles || []).map((detalleLocal) => [getDetalleIdentity(detalleLocal), detalleLocal])
  );
  const persistedIdentities = new Set();

  const mergedPersistedDetails = (nextPersisted?.detalles || []).map((detallePersistido) => {
    const bindingIdentity = getPersistedDetalleBindingIdentity(detallePersistido);
    persistedIdentities.add(bindingIdentity);

    const detalleLocal = localByIdentity.get(bindingIdentity);
    if (!detalleLocal) {
      return detallePersistido;
    }

    return {
      ...detallePersistido,
      ...(detalleLocal?.optimisticKey
        ? {
            optimisticKey: detalleLocal.optimisticKey,
            clientDetailKey: detallePersistido?.clientDetailKey || detalleLocal.optimisticKey,
          }
        : {}),
      ...buildLocalDetalleOverlay(detalleLocal),
    };
  });

  const optimisticLocalDetails = (prevLocal?.detalles || []).filter((detalleLocal) => {
    if (detalleLocal?.estado === "CANCELADO") return false;

    const identity = getDetalleIdentity(detalleLocal);
    if (persistedIdentities.has(identity)) return false;

    const existiaEnPersistido = (prevPersisted?.detalles || []).some(
      (detalle) => getPersistedDetalleBindingIdentity(detalle) === identity
    );

    return !existiaEnPersistido;
  });

  return {
    ...nextPersisted,
    mozo: prevLocal?.mozo ?? nextPersisted?.mozo ?? null,
    paraLlevar: prevLocal?.paraLlevar ?? nextPersisted?.paraLlevar ?? false,
    comentario: prevLocal?.comentario ?? nextPersisted?.comentario ?? "",
    detalles: [...mergedPersistedDetails, ...optimisticLocalDetails],
  };
};

const findPersistedDetalleByLocalIdentity = (detalles = [], localIdentity) =>
  (detalles || []).find(
    (detalle) => detalle?.clientDetailKey === localIdentity || getDetalleIdentity(detalle) === localIdentity
  );

const buildPedidoDraftStorageKey = ({ mesaId, userId }) =>
  `${PEDIDO_DRAFT_STORAGE_PREFIX}:mesa:${mesaId || "na"}:user:${userId || "na"}`;

const readPedidoDraftSession = (storageKey) => {
  if (!storageKey) return null;

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue);
    if (Number(parsed?.version || 0) !== PEDIDO_DRAFT_STORAGE_VERSION) {
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("No se pudo leer el borrador local del pedido:", error);
    return null;
  }
};

const writePedidoDraftSession = (storageKey, payload) => {
  if (!storageKey) return;

  try {
    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        version: PEDIDO_DRAFT_STORAGE_VERSION,
        ...payload,
      })
    );
  } catch (error) {
    console.error("No se pudo persistir el borrador local del pedido:", error);
  }
};

const removePedidoDraftSession = (storageKey) => {
  if (!storageKey) return;

  try {
    window.localStorage.removeItem(storageKey);
  } catch (error) {
    console.error("No se pudo eliminar el borrador local del pedido:", error);
  }
};

const createOutboxToken = () => `${Date.now()}:${Math.random().toString(36).slice(2, 8)}`;

const upsertOutboxItem = (items = [], nextItem) => {
  const index = items.findIndex((item) => item?.localIdentity === nextItem?.localIdentity);

  if (index === -1) {
    return [...items, nextItem];
  }

  const mergedItem = {
    ...items[index],
    ...nextItem,
  };

  return items.map((item, itemIndex) => (itemIndex === index ? mergedItem : item));
};

const findDetalleByIdentity = (detalles = [], localIdentity) =>
  (detalles || []).find((detalle) => getDetalleIdentity(detalle) === localIdentity);

const parseDecimalValue = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;

  const normalizedValue = String(rawValue).trim();
  if (!normalizedValue) return null;

  if (normalizedValue.includes(".") && normalizedValue.includes(",")) {
    return Number(normalizedValue.replace(/\./g, "").replace(",", "."));
  }

  if (normalizedValue.includes(",")) {
    return Number(normalizedValue.replace(",", "."));
  }

  return Number(normalizedValue);
};

const getGraphQLErrorMessages = (error) => {
  const messages = [
    ...(error?.graphQLErrors || []).map((item) => item?.message),
    ...(error?.networkError?.result?.errors || []).map((item) => item?.message),
    error?.message,
  ].filter(Boolean);

  return [...new Set(messages)];
};

const parseStockConflictError = (error) => {
  const graphQLErrors = [...(error?.graphQLErrors || []), ...(error?.networkError?.result?.errors || [])];
  const matchingError = graphQLErrors.find(
    (item) =>
      item?.data?.tipo === "STOCK_INSUFICIENTE" ||
      (/Stock agotado para materia prima/i.test(item?.message || "") &&
        /Disponible:/i.test(item?.message || "") &&
        /requerido:/i.test(item?.message || ""))
  );
  const matchingMessage =
    matchingError?.message ||
    getGraphQLErrorMessages(error).find(
      (message) =>
        /Stock agotado para materia prima/i.test(message) && /Disponible:/i.test(message) && /requerido:/i.test(message)
    );

  if (!matchingMessage) {
    return null;
  }

  const conflictMatch = matchingMessage.match(
    /Stock agotado para materia prima\s+(\d+)\s+en sucursal\s+\d+\.\s+Disponible:\s*([0-9.,]+),\s*requerido:\s*([0-9.,]+)/i
  );

  if (!conflictMatch) {
    return null;
  }

  const [, idMateriaPrima, disponibleRaw, requeridoRaw] = conflictMatch;

  return {
    message: matchingMessage,
    idMateriaPrima: Number(idMateriaPrima || 0),
    disponible: parseDecimalValue(disponibleRaw),
    requerido: parseDecimalValue(requeridoRaw),
    disponibleRaw,
    requeridoRaw,
    ingredientesFaltantes: Array.isArray(matchingError?.data?.ingredientesFaltantes)
      ? matchingError.data.ingredientesFaltantes
      : [],
    cantidadAjustadaDisponible: Number.isFinite(Number(matchingError?.data?.cantidadAjustadaDisponible))
      ? Number(matchingError.data.cantidadAjustadaDisponible)
      : null,
  };
};

const formatStockQuantity = (value) =>
  new Intl.NumberFormat("es-AR", {
    maximumFractionDigits: 3,
  }).format(Number(value || 0));

const IngredientesFaltantesTable = ({ ingredientes = [] }) => (
  <TableContainer
    sx={{
      mt: 0.5,
      maxWidth: "100%",
      border: "1px solid rgba(15, 23, 42, 0.12)",
      borderRadius: 2,
    }}
  >
    <Table
      size="small"
      aria-label="Ingredientes faltantes"
      sx={{
        minWidth: { xs: 580, sm: 0 },
        width: "100%",
        tableLayout: "fixed",
        "& .MuiTableCell-root": {
          display: "table-cell",
          width: "20%",
          verticalAlign: "middle",
          overflowWrap: "anywhere",
        },
      }}
    >
      <TableHead sx={{ display: "table-header-group" }}>
        <TableRow sx={{ display: "table-row" }}>
          {["Ingrediente", "Receta", "Porciones pedidas", "Stock disponible", "Total requerido"].map((label) => (
            <TableCell key={label} sx={{ px: 1, py: 0.75, backgroundColor: "#f8fafc" }}>
              <SoftTypography variant="caption" fontWeight="bold" color="dark">
                {label}
              </SoftTypography>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody sx={{ display: "table-row-group" }}>
        {ingredientes.map((ingrediente) => (
          <TableRow key={ingrediente.idMateriaPrima} sx={{ display: "table-row" }}>
            <TableCell sx={{ px: 1, py: 0.75 }}>
              <SoftTypography variant="caption" fontWeight="bold" color="dark">
                {ingrediente.nombre}
              </SoftTypography>
            </TableCell>
            <TableCell sx={{ px: 1, py: 0.75 }}>
              <SoftTypography variant="caption" color="dark">
                {formatStockQuantity(ingrediente.cantidadReceta)} {ingrediente.unidad} /{" "}
                {formatStockQuantity(ingrediente.porcionesReceta)} porc.
              </SoftTypography>
            </TableCell>
            <TableCell sx={{ px: 1, py: 0.75 }}>
              <SoftTypography variant="caption" color="dark">
                {formatStockQuantity(ingrediente.porcionesPedido)}
              </SoftTypography>
            </TableCell>
            <TableCell sx={{ px: 1, py: 0.75 }}>
              <SoftTypography variant="caption" color="error" fontWeight="bold">
                {formatStockQuantity(ingrediente.stockDisponible)} {ingrediente.unidad}
              </SoftTypography>
            </TableCell>
            <TableCell sx={{ px: 1, py: 0.75 }}>
              <SoftTypography variant="caption" color="dark" fontWeight="bold">
                {formatStockQuantity(ingrediente.totalRequerido)} {ingrediente.unidad}
              </SoftTypography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

IngredientesFaltantesTable.propTypes = {
  ingredientes: PropTypes.arrayOf(PropTypes.object),
};

export default function ModalPedido({
  open,
  handleClose,
  mesaElegida,
  refetch,
  savePedido: _savePedido,
  loadingSave,
  stockRefreshToken = 0,
}) {
  const { handleSnackbar } = useContext(MessageManager);
  const { confirmar } = useContext(ConfirmarContext);
  const { menu } = useContext(MenuContext);
  const { user } = useContext(UserContext);
  const fallbackMozoId = user?.tipoUser === "Mozo" ? user?.id : null;

  const [total, setTotal] = useState(0);
  const [modalFacturar, setModalFacturar] = useState(false);
  const [mozoData, setMozoData] = useState(null);
  const [id, setID] = useState(null);
  const [pedido, setPedido] = useState(() => createEmptyPedidoState(fallbackMozoId));
  const [pedidoOriginal, setPedidoOriginal] = useState(() => createEmptyPedidoState(fallbackMozoId));
  const [pedidoPersistido, setPedidoPersistido] = useState(() => createEmptyPedidoState(fallbackMozoId));

  const pedidoRef = useRef(pedido);
  const pedidoPersistidoRef = useRef(pedidoPersistido);
  const pedidoOriginalRef = useRef(pedidoOriginal);
  const stockRefreshTimeoutRef = useRef(null);

  const [runSavePedido, { loading: loadingMutationSave }] = useMutation(SAVE_PEDIDO_MUTATION);
  const [runSyncDetallePedidoMesa] = useMutation(SYNC_DETALLE_PEDIDO_MESA_MUTATION);
  const [deletePedidoMutation, { loading: loadingDeletePedido }] = useMutation(DELETE_PEDIDO_MUTATION);
  const [isFinalActionPending, setIsFinalActionPending] = useState(false);
  const [isCloseActionPending, setIsCloseActionPending] = useState(false);
  const [liveStockRefreshTick, setLiveStockRefreshTick] = useState(0);
  const [draftOutbox, setDraftOutbox] = useState([]);
  const [draftSyncStatus, setDraftSyncStatus] = useState({
    isSyncing: false,
    pendingCount: 0,
    lastError: null,
  });

  const draftStorageKey = useMemo(
    () =>
      buildPedidoDraftStorageKey({
        mesaId: mesaElegida?.id,
        userId: user?.id,
      }),
    [mesaElegida?.id, user?.id]
  );
  const draftOutboxRef = useRef(draftOutbox);
  const draftSyncStatusRef = useRef(draftSyncStatus);
  const outboxProcessingRef = useRef(null);
  const outboxSyncTimeoutRef = useRef(null);
  const draftPersistTimeoutRef = useRef(null);
  const shouldPersistDraftRef = useRef(false);
  const abortDraftSyncRef = useRef(false);
  const stockConflictResolutionRef = useRef(null);
  const resolveStockConflictHandlerRef = useRef(() => false);

  const { data: dataMozos } = useQuery(
    gql`
      query getMozos($filter: JSON) {
        usuarios(filter: $filter, order: { field: "nombre", order: ASC }) {
          id
          nombre
          apellido
        }
      }
    `,
    {
      variables: {
        filter: { tipoUser: "Mozo" },
      },
      fetchPolicy: "cache-first",
    }
  );

  const [marcarDetallesComoImpresos] = useMutation(
    gql`
      mutation marcarDetallesComoImpresos($idsDetalles: [ID!]!) {
        marcarDetallesComoImpresos(idsDetalles: $idsDetalles) {
          success
          message
          fechaImpresion
        }
      }
    `
  );

  const { data: nuevoPedidoStockEvent } = useSubscription(NUEVO_PEDIDO_STOCK_SUBSCRIPTION, {
    skip: !open,
  });
  const { data: pedidoModificadoStockEvent } = useSubscription(PEDIDO_MODIFICADO_STOCK_SUBSCRIPTION, {
    skip: !open,
  });
  const { data: pedidoEliminadoStockEvent } = useSubscription(PEDIDO_ELIMINADO_STOCK_SUBSCRIPTION, {
    skip: !open,
  });

  useEffect(() => {
    pedidoRef.current = pedido;
  }, [pedido]);

  useEffect(() => {
    pedidoPersistidoRef.current = pedidoPersistido;
  }, [pedidoPersistido]);

  useEffect(() => {
    pedidoOriginalRef.current = pedidoOriginal;
  }, [pedidoOriginal]);

  useEffect(() => {
    draftOutboxRef.current = draftOutbox;
  }, [draftOutbox]);

  useEffect(() => {
    draftSyncStatusRef.current = draftSyncStatus;
  }, [draftSyncStatus]);

  useEffect(() => {
    if (open) {
      abortDraftSyncRef.current = false;
    }
  }, [open, mesaElegida?.id]);

  useEffect(
    () => () => {
      if (stockRefreshTimeoutRef.current) {
        clearTimeout(stockRefreshTimeoutRef.current);
      }
      if (outboxSyncTimeoutRef.current) {
        clearTimeout(outboxSyncTimeoutRef.current);
      }
      if (draftPersistTimeoutRef.current) {
        clearTimeout(draftPersistTimeoutRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!mesaElegida) return;

    const mozoInicial = mesaElegida?.pedido?.mozo || (user?.tipoUser === "Mozo" ? user : null);
    const pedidoInicial = normalizePedidoState(mesaElegida?.pedido, fallbackMozoId);
    const draftSession = readPedidoDraftSession(draftStorageKey);
    const draftMesaId = Number(draftSession?.mesaId || 0);
    const hasDraftForCurrentMesa = draftMesaId && draftMesaId === Number(mesaElegida?.id || 0);
    const pedidoOriginalDraft = hasDraftForCurrentMesa
      ? normalizePedidoState(draftSession?.pedidoOriginal, fallbackMozoId)
      : pedidoInicial;
    const pedidoPersistidoDraft = hasDraftForCurrentMesa
      ? normalizePedidoState(draftSession?.pedidoPersistido, fallbackMozoId)
      : pedidoInicial;
    const pedidoDraft = hasDraftForCurrentMesa
      ? normalizePedidoState(draftSession?.pedido, fallbackMozoId)
      : pedidoInicial;

    setMozoData(mozoInicial);
    setPedidoOriginal(pedidoOriginalDraft);
    setPedidoPersistido(pedidoPersistidoDraft);
    setPedido(pedidoDraft);
    setDraftOutbox(hasDraftForCurrentMesa ? draftSession?.outbox || [] : []);
    setDraftSyncStatus({
      isSyncing: false,
      pendingCount: hasDraftForCurrentMesa ? (draftSession?.outbox || []).length : 0,
      lastError: hasDraftForCurrentMesa ? draftSession?.lastSyncError || null : null,
    });
    shouldPersistDraftRef.current = true;
    setModalFacturar(false);
    setID(null);
  }, [draftStorageKey, fallbackMozoId, mesaElegida, user]);

  useEffect(() => {
    if (!pedido?.mozo || !(dataMozos?.usuarios || []).length) return;

    const mozoActual = (dataMozos?.usuarios || []).find((mozo) => Number(mozo?.id) === Number(pedido?.mozo));

    if (mozoActual) {
      setMozoData(mozoActual);
    }
  }, [dataMozos?.usuarios, pedido?.mozo]);

  const isPedidoReadOnly = useMemo(
    () => !!(mesaElegida?.pedido?.id && user?.tipoUser === "Mozo" && user?.id !== mesaElegida?.pedido?.mozo?.id),
    [mesaElegida?.pedido?.id, mesaElegida?.pedido?.mozo?.id, user?.id, user?.tipoUser]
  );

  const hydratePedidoDesdeServidor = useCallback(
    (pedidoServidor, fallbackState = null) => {
      const pedidoNormalizado = normalizePedidoState(pedidoServidor, fallbackState?.mozo || fallbackMozoId || null);

      if (!pedidoNormalizado?.mozo && fallbackState?.mozo) {
        pedidoNormalizado.mozo = fallbackState.mozo;
      }

      return pedidoNormalizado;
    },
    [fallbackMozoId]
  );

  const clearDraftSession = useCallback(() => {
    shouldPersistDraftRef.current = false;
    setDraftOutbox([]);
    setDraftSyncStatus({
      isSyncing: false,
      pendingCount: 0,
      lastError: null,
    });
    removePedidoDraftSession(draftStorageKey);
  }, [draftStorageKey]);

  const enqueueDetalleSync = useCallback((detalle, meta = {}) => {
    const localIdentity = meta?.localIdentity || getDetalleIdentity(detalle);
    if (!localIdentity) return;

    setDraftOutbox((prevItems) =>
      upsertOutboxItem(prevItems, {
        localIdentity,
        token: createOutboxToken(),
        idProducto: Number(detalle?.producto?.id || detalle?.idProducto || meta?.idProducto || 0),
        createdAt: new Date().toISOString(),
      })
    );
  }, []);

  const processDraftOutbox = useCallback(async () => {
    if (outboxProcessingRef.current) {
      return outboxProcessingRef.current;
    }

    outboxProcessingRef.current = (async () => {
      if (abortDraftSyncRef.current) {
        setDraftOutbox([]);
        return;
      }

      const currentOp = draftOutboxRef.current[0];
      if (!currentOp) return;

      const pedidoActual = pedidoRef.current;
      const pedidoPersistidoActual = pedidoPersistidoRef.current;
      const mozoId = Number(pedidoActual?.mozo || 0);

      if (!mozoId) {
        return;
      }

      const detalleActual = findDetalleByIdentity(pedidoActual?.detalles || [], currentOp?.localIdentity);

      if (!detalleActual) {
        setDraftOutbox((prevItems) => prevItems.filter((item) => item?.localIdentity !== currentOp?.localIdentity));
        return;
      }

      if (detalleActual?.estado === "CANCELADO" && !detalleActual?.id) {
        setDraftOutbox((prevItems) => prevItems.filter((item) => item?.localIdentity !== currentOp?.localIdentity));
        return;
      }

      setDraftSyncStatus((prevState) => ({
        ...prevState,
        isSyncing: true,
        lastError: null,
      }));

      try {
        const detalleInput = {
          ...buildDetalleInputForSync(detalleActual, pedidoPersistidoActual),
          clientDetailKey: currentOp?.localIdentity || detalleActual?.clientDetailKey || null,
        };

        const { data } = await runSyncDetallePedidoMesa({
          variables: {
            input: {
              idPedido: pedidoPersistidoActual?.id || null,
              idMesa: mesaElegida?.id,
              idMozo: mozoId,
              idSede: Number(pedidoActual?.idSede || user?.sede?.id || 1),
              detalle: detalleInput,
            },
          },
        });

        if (abortDraftSyncRef.current) {
          setDraftOutbox([]);
          return;
        }

        const pedidoServidor = hydratePedidoDesdeServidor(data?.syncDetallePedidoMesa, pedidoPersistidoActual);
        const latestLocalPedido = {
          ...(pedidoRef.current || {}),
          detalles: pedidoRef.current?.detalles || [],
        };
        const pedidoMergeado = mergeLocalNonPersistedChanges({
          nextPersisted: pedidoServidor,
          prevLocal: latestLocalPedido,
          prevPersisted: pedidoPersistidoActual,
        });

        setPedidoPersistido(pedidoServidor);
        setPedido(pedidoMergeado);

        const detalleLocalPostMerge =
          findDetalleByIdentity(pedidoMergeado?.detalles || [], currentOp?.localIdentity) ||
          (pedidoMergeado?.detalles || []).find(
            (detalle) => Number(detalle?.id || 0) && `id:${detalle.id}` === currentOp?.localIdentity
          );
        const detallePersistidoPostSync =
          findPersistedDetalleByLocalIdentity(pedidoServidor?.detalles || [], currentOp?.localIdentity) ||
          (detalleLocalPostMerge?.id
            ? (pedidoServidor?.detalles || []).find(
                (detalle) => Number(detalle?.id || 0) === Number(detalleLocalPostMerge?.id || 0)
              )
            : null);

        if (
          detalleLocalPostMerge &&
          detallePersistidoPostSync &&
          !areDetalleSyncComparablesEqual(detalleLocalPostMerge, detallePersistidoPostSync)
        ) {
          enqueueDetalleSync(detalleLocalPostMerge, {
            localIdentity: currentOp?.localIdentity,
            idProducto: Number(detalleLocalPostMerge?.producto?.id || detalleLocalPostMerge?.idProducto || 0),
          });
        }

        setDraftOutbox((prevItems) => {
          const nextItems = [...prevItems];
          const currentIndex = nextItems.findIndex((item) => item?.localIdentity === currentOp?.localIdentity);

          if (currentIndex === -1) {
            return prevItems;
          }

          if (nextItems[currentIndex]?.token !== currentOp?.token) {
            return prevItems;
          }

          nextItems.splice(currentIndex, 1);
          return nextItems;
        });

        setTimeout(() => {
          if (outboxSyncTimeoutRef.current) {
            clearTimeout(outboxSyncTimeoutRef.current);
          }

          outboxSyncTimeoutRef.current = setTimeout(() => {
            outboxSyncTimeoutRef.current = null;
            void processDraftOutbox();
          }, 0);
        }, 0);
      } catch (error) {
        const stockConflict = parseStockConflictError(error);
        const stockConflictHandled =
          stockConflict &&
          (await resolveStockConflictHandlerRef.current({
            currentOp,
            detalleActual,
            stockConflict,
          }));

        if (stockConflictHandled) {
          return;
        }

        setDraftSyncStatus((prevState) => ({
          ...prevState,
          isSyncing: false,
          lastError: error?.message || String(error),
        }));
      }
    })().finally(() => {
      outboxProcessingRef.current = null;
      setDraftSyncStatus((prevState) => ({
        ...prevState,
        isSyncing: false,
      }));
    });

    return outboxProcessingRef.current;
  }, [enqueueDetalleSync, hydratePedidoDesdeServidor, mesaElegida?.id, runSyncDetallePedidoMesa, user?.sede?.id]);

  const scheduleDraftOutboxProcessing = useCallback(
    (delayMs = PEDIDO_OUTBOX_SYNC_DEBOUNCE_MS) => {
      if (outboxSyncTimeoutRef.current) {
        clearTimeout(outboxSyncTimeoutRef.current);
      }

      outboxSyncTimeoutRef.current = setTimeout(() => {
        outboxSyncTimeoutRef.current = null;
        void processDraftOutbox();
      }, delayMs);
    },
    [processDraftOutbox]
  );

  const scheduleLiveStockRefresh = useCallback(() => {
    if (stockRefreshTimeoutRef.current) return;

    stockRefreshTimeoutRef.current = setTimeout(() => {
      setLiveStockRefreshTick((prev) => prev + 1);
      stockRefreshTimeoutRef.current = null;
    }, 180);
  }, []);

  useEffect(() => {
    setDraftSyncStatus((prevState) => ({
      ...prevState,
      pendingCount: draftOutbox.length,
    }));
  }, [draftOutbox.length]);

  useEffect(() => {
    if (!open || !shouldPersistDraftRef.current || !draftStorageKey) {
      return;
    }

    if (draftPersistTimeoutRef.current) {
      clearTimeout(draftPersistTimeoutRef.current);
    }

    draftPersistTimeoutRef.current = setTimeout(() => {
      writePedidoDraftSession(draftStorageKey, {
        mesaId: mesaElegida?.id || null,
        pedido: pedidoRef.current,
        pedidoPersistido: pedidoPersistidoRef.current,
        pedidoOriginal: pedidoOriginalRef.current,
        outbox: draftOutboxRef.current,
        lastSyncError: draftSyncStatus?.lastError || null,
        updatedAt: new Date().toISOString(),
      });
      draftPersistTimeoutRef.current = null;
    }, PEDIDO_DRAFT_PERSIST_DEBOUNCE_MS);

    return () => {
      if (draftPersistTimeoutRef.current) {
        clearTimeout(draftPersistTimeoutRef.current);
      }
    };
  }, [
    draftOutbox,
    draftStorageKey,
    draftSyncStatus?.lastError,
    mesaElegida?.id,
    open,
    pedido,
    pedidoOriginal,
    pedidoPersistido,
  ]);

  useEffect(() => {
    if (!open || draftOutbox.length === 0) return;

    scheduleDraftOutboxProcessing();
  }, [draftOutbox.length, open, scheduleDraftOutboxProcessing]);

  useEffect(() => {
    if (!nuevoPedidoStockEvent?.nuevoPedido) return;
    scheduleLiveStockRefresh();
  }, [nuevoPedidoStockEvent, scheduleLiveStockRefresh]);

  useEffect(() => {
    if (!pedidoModificadoStockEvent?.pedidoModificado) return;
    scheduleLiveStockRefresh();
  }, [pedidoModificadoStockEvent, scheduleLiveStockRefresh]);

  useEffect(() => {
    const pedidoEliminado = pedidoEliminadoStockEvent?.pedidoEliminado;
    if (!pedidoEliminado) return;

    scheduleLiveStockRefresh();
    refetch?.();

    const idPedidoActual = Number(pedidoPersistidoRef.current?.id || pedidoRef.current?.id || 0);
    if (idPedidoActual && idPedidoActual === Number(pedidoEliminado?.id || 0)) {
      const pedidoVacio = createEmptyPedidoState(pedidoOriginalRef.current?.mozo || fallbackMozoId);
      setPedidoOriginal(pedidoVacio);
      setPedidoPersistido(pedidoVacio);
      setPedido(pedidoVacio);
    }
  }, [fallbackMozoId, pedidoEliminadoStockEvent, refetch, scheduleLiveStockRefresh]);

  const detallesActivos = useMemo(
    () => (pedido?.detalles || []).filter((detalle) => detalle?.estado !== "CANCELADO"),
    [pedido?.detalles]
  );

  const hasRealChangesCurrent = useMemo(
    () =>
      hasRealChangesBetweenStates({
        pedidoOriginalState: pedidoOriginal,
        pedidoState: pedido,
        pedidoPersistidoState: pedidoPersistido,
      }),
    [pedido, pedidoOriginal, pedidoPersistido]
  );

  const hasRealChanges = useCallback(() => {
    return hasRealChangesBetweenStates({
      pedidoOriginalState: pedidoOriginalRef.current,
      pedidoState: pedidoRef.current,
      pedidoPersistidoState: pedidoPersistidoRef.current,
    });
  }, []);

  const flushDraftOutbox = useCallback(
    async ({ showError = false } = {}) => {
      if (outboxSyncTimeoutRef.current) {
        clearTimeout(outboxSyncTimeoutRef.current);
        outboxSyncTimeoutRef.current = null;
      }

      let attempts = 0;
      while (draftOutboxRef.current.length > 0) {
        attempts += 1;
        await processDraftOutbox();
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (draftSyncStatusRef.current?.lastError) {
          throw new Error(draftSyncStatusRef.current.lastError);
        }

        if (attempts >= 200) {
          throw new Error("Se excedio el tiempo de espera al sincronizar los cambios pendientes.");
        }
      }

      if (showError && draftSyncStatusRef.current?.lastError) {
        throw new Error(draftSyncStatusRef.current.lastError);
      }
    },
    [processDraftOutbox]
  );

  const sincronizarDetalleMesa = useCallback(
    async ({ detalleInput, pendingProductId = null, pendingDetalleId = null }) => {
      const pedidoActual = pedidoRef.current;
      const detalleLocal =
        findDetalleByIdentity(pedidoActual?.detalles || [], pendingDetalleId) ||
        (pedidoActual?.detalles || []).find(
          (detalle) =>
            Number(detalle?.producto?.id || detalle?.idProducto) ===
              Number(detalleInput?.idProducto || pendingProductId || 0) && detalle?.estado !== "CANCELADO"
        );

      const detalleAEncolar = detalleLocal || detalleInput;
      const localIdentity =
        pendingDetalleId ||
        getDetalleIdentity(detalleLocal) ||
        (detalleInput?.id ? `id:${detalleInput.id}` : `prod:${detalleInput?.idProducto || "na"}:comanda:na`);

      enqueueDetalleSync(detalleAEncolar, {
        idProducto: pendingProductId,
        localIdentity,
      });
      scheduleDraftOutboxProcessing();
      return Promise.resolve();
    },
    [enqueueDetalleSync, scheduleDraftOutboxProcessing]
  );

  const handleCloseAfterCleanup = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleDiscardSessionChanges = useCallback(async () => {
    setIsCloseActionPending(true);
    try {
      if (outboxSyncTimeoutRef.current) {
        clearTimeout(outboxSyncTimeoutRef.current);
        outboxSyncTimeoutRef.current = null;
      }
      const pedidoActual = pedidoRef.current;
      const pedidoPersistidoActual = pedidoPersistidoRef.current;
      const pedidoOriginalActual = pedidoOriginalRef.current;
      const debeEliminarPedidoCompleto = shouldDeletePedidoOnClose({
        pedidoState: pedidoActual,
        pedidoPersistidoState: pedidoPersistidoActual,
      });

      if (debeEliminarPedidoCompleto) {
        abortDraftSyncRef.current = true;
        setDraftOutbox([]);
        setDraftSyncStatus({
          isSyncing: false,
          pendingCount: 0,
          lastError: null,
        });
        await deletePedidoMutation({
          variables: {
            id: pedidoPersistidoActual.id,
          },
        });
        await refetch?.();
        clearDraftSession();
        handleCloseAfterCleanup();
        return;
      }

      abortDraftSyncRef.current = false;
      await flushDraftOutbox();

      if (pedidoOriginalActual?.id) {
        const pedidoRevertido = buildPedidoStateForDiscard({
          pedidoOriginalState: pedidoOriginalActual,
          pedidoPersistidoState: pedidoPersistidoActual,
        });

        await runSavePedido({
          variables: {
            input: buildPedidoInput({
              pedidoState: pedidoRevertido,
              mesaId: mesaElegida?.id,
              idSede: Number(pedidoOriginalActual?.idSede || user?.sede?.id || 1),
            }),
          },
        });
        await refetch?.();
        clearDraftSession();
        handleCloseAfterCleanup();
        return;
      }

      clearDraftSession();
      handleCloseAfterCleanup();
    } finally {
      setIsCloseActionPending(false);
    }
  }, [
    clearDraftSession,
    deletePedidoMutation,
    flushDraftOutbox,
    handleCloseAfterCleanup,
    mesaElegida?.id,
    refetch,
    runSavePedido,
    user?.sede?.id,
  ]);

  const handleCloseWithValidationLegacy = useCallback(() => {
    if (hasRealChanges()) {
      const esPedidoCreadoEnSesion = !pedidoOriginalRef.current?.id && !!pedidoPersistidoRef.current?.id;

      confirmar({
        title: "Cambios sin guardar",
        message: esPedidoCreadoEnSesion
          ? "Si continua la meza se cancelará automáticamente sin crear el pedido ¿desea continuar?"
          : "Si continua se descartarán los cambios de esta sesión y se restaurará el pedido original ¿desea continuar?",
        icon: "warning",
        func: async () => {
          try {
            await handleDiscardSessionChanges();
          } catch (error) {
            handleSnackbar(`No se pudieron descartar los cambios: ${error?.message || error}`, "error");
          }
        },
      });
      return;
    }

    clearDraftSession();
    handleClose();
  }, [clearDraftSession, confirmar, handleClose, handleDiscardSessionChanges, handleSnackbar, hasRealChanges]);

  void handleCloseWithValidationLegacy;

  const handleCloseWithValidationEffective = useCallback(() => {
    if (hasRealChanges()) {
      const pedidoActual = pedidoRef.current;
      const pedidoPersistidoActual = pedidoPersistidoRef.current;
      const debeAbandonarMesa = shouldDeletePedidoOnClose({
        pedidoState: pedidoActual,
        pedidoPersistidoState: pedidoPersistidoActual,
      });

      confirmar({
        title: debeAbandonarMesa ? "Abandonar mesa" : "Cambios sin guardar",
        message: debeAbandonarMesa
          ? "La mesa quedara sin productos. Desea abandonar la mesa sin agregar productos? Si confirma se eliminara el pedido y sus detalles."
          : "Si continua se descartaran los cambios de esta sesion y se restaurara el pedido original. Desea continuar?",
        icon: "warning",
        func: async () => {
          try {
            await handleDiscardSessionChanges();
          } catch (error) {
            handleSnackbar(`No se pudieron descartar los cambios: ${error?.message || error}`, "error");
          }
        },
      });
      return;
    }

    clearDraftSession();
    handleClose();
  }, [clearDraftSession, confirmar, handleClose, handleDiscardSessionChanges, handleSnackbar, hasRealChanges]);

  const handleSelectMozo = useCallback(
    (mozoSeleccionado) => {
      setMozoData(mozoSeleccionado || null);
      setPedido((prevPedido) => ({
        ...prevPedido,
        mozo: mozoSeleccionado?.id || null,
      }));
      if (draftOutboxRef.current.length > 0) {
        scheduleDraftOutboxProcessing(0);
      }
    },
    [scheduleDraftOutboxProcessing]
  );

  const loadingSaveEffective =
    loadingSave || loadingMutationSave || loadingDeletePedido || isFinalActionPending || isCloseActionPending;

  const handleOpenMozosMenu = useCallback(
    (event) => {
      if (user?.tipoUser === "Mozo" || isPedidoReadOnly || loadingSaveEffective) return;

      menu({
        open: event.currentTarget,
        align: "center",
        options: (dataMozos?.usuarios || []).map((mozo) => ({
          name: `${mozo.nombre} ${mozo.apellido}`,
          icon: {
            icon: pedido?.mozo === mozo.id ? "check_circle" : "person",
            color: pedido?.mozo === mozo.id ? "success" : "info",
          },
          onClick: () => handleSelectMozo(mozo),
        })),
      });
    },
    [dataMozos?.usuarios, handleSelectMozo, isPedidoReadOnly, loadingSaveEffective, menu, pedido?.mozo, user?.tipoUser]
  );

  const handleChangePedido = useCallback((event) => {
    const { name, value } = event.target;

    setPedido((prevPedido) => ({
      ...prevPedido,
      [name]: value,
    }));
  }, []);

  const getDetalleUnitPrice = useCallback(
    (detalle, cantidad) => {
      const cantidadPorMayor = Number(detalle?.producto?.cantidadPorMayor || 0);
      const paraLlevarActivo = pedido?.paraLlevar === 1 || !!pedido?.paraLlevar;

      if (paraLlevarActivo) {
        if (cantidadPorMayor > 0 && cantidad >= cantidadPorMayor) {
          return Number(detalle?.producto?.precioPorMayorOnline ?? 0);
        }

        return Number(detalle?.producto?.precioOnline ?? 0);
      }

      if (cantidadPorMayor > 0 && cantidad >= cantidadPorMayor) {
        return Number(detalle?.producto?.precioPorMayor ?? detalle?.producto?.precioCarta ?? 0);
      }

      return Number(detalle?.producto?.precioCarta ?? 0);
    },
    [pedido?.paraLlevar]
  );

  const removeDetalleLocally = useCallback(
    (detalle, motivoCancelacion = null) => {
      if (!detalle) return;

      const detalleIdentity = getDetalleIdentity(detalle);
      const esDetallePersistido = !!detalle?.id;

      setPedido((prevPedido) => ({
        ...prevPedido,
        detalles: esDetallePersistido
          ? (prevPedido?.detalles || []).map((detalleActual) =>
              getDetalleIdentity(detalleActual) === detalleIdentity
                ? {
                    ...detalleActual,
                    estado: "CANCELADO",
                    motivoCancelacion,
                  }
                : detalleActual
            )
          : (prevPedido?.detalles || []).filter(
              (detalleActual) => getDetalleIdentity(detalleActual) !== detalleIdentity
            ),
      }));

      if (esDetallePersistido) {
        enqueueDetalleSync(
          {
            ...detalle,
            estado: "CANCELADO",
            motivoCancelacion,
          },
          { localIdentity: detalleIdentity }
        );
        scheduleDraftOutboxProcessing();
        return;
      }

      setDraftOutbox((prevItems) => prevItems.filter((outboxItem) => outboxItem?.localIdentity !== detalleIdentity));
    },
    [enqueueDetalleSync, scheduleDraftOutboxProcessing]
  );

  const updateDetalleQuantityLocally = useCallback(
    (detalle, cantidadObjetivo) => {
      if (!detalle) return;

      const nuevaCantidad = Number(cantidadObjetivo || 0);
      if (nuevaCantidad <= 0) {
        removeDetalleLocally(detalle, null);
        return;
      }

      const detalleIdentity = getDetalleIdentity(detalle);
      const detalleActualizado = {
        ...detalle,
        cantidad: nuevaCantidad,
        precio: getDetalleUnitPrice(detalle, nuevaCantidad),
      };

      setPedido((prevPedido) => ({
        ...prevPedido,
        detalles: (prevPedido?.detalles || []).map((detalleActual) =>
          getDetalleIdentity(detalleActual) === detalleIdentity ? detalleActualizado : detalleActual
        ),
      }));

      enqueueDetalleSync(detalleActualizado, {
        localIdentity: detalleIdentity,
        idProducto: Number(detalle?.producto?.id || detalle?.idProducto || 0),
      });
      scheduleDraftOutboxProcessing();
    },
    [enqueueDetalleSync, getDetalleUnitPrice, removeDetalleLocally, scheduleDraftOutboxProcessing]
  );

  const resolveStockConflict = useCallback(
    ({ currentOp, detalleActual, stockConflict }) => {
      if (!currentOp?.localIdentity || !detalleActual || !stockConflict) {
        return Promise.resolve(false);
      }

      const resolutionKey = `${currentOp.localIdentity}:${stockConflict?.disponibleRaw || ""}:${
        stockConflict?.requeridoRaw || ""
      }`;

      if (stockConflictResolutionRef.current === resolutionKey) {
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        stockConflictResolutionRef.current = resolutionKey;

        const ingredientesFaltantes = stockConflict?.ingredientesFaltantes || [];
        const esProductoNoElaboradoConDetalle =
          !detalleActual?.esElaborado && !detalleActual?.producto?.esElaborado && ingredientesFaltantes.length > 0;
        const cantidadAjustada = esProductoNoElaboradoConDetalle
          ? Number(stockConflict?.cantidadAjustadaDisponible || 0)
          : Number(stockConflict?.disponible || 0);

        const handleAfterDecision = (resolver) => {
          stockConflictResolutionRef.current = null;
          setDraftSyncStatus((prevState) => ({
            ...prevState,
            lastError: null,
          }));

          const detalleVigente =
            findDetalleByIdentity(pedidoRef.current?.detalles || [], currentOp.localIdentity) || detalleActual;

          resolver(detalleVigente);
          resolve(true);
        };

        confirmar({
          title: esProductoNoElaboradoConDetalle
            ? "Ingredientes sin stock suficiente"
            : "Stock insuficiente al sincronizar",
          icon: "warning",
          confirmLabel: esProductoNoElaboradoConDetalle
            ? cantidadAjustada > 0
              ? `Ajustar pedido a ${cantidadAjustada}`
              : "Quitar producto"
            : "Ajustar a disponible",
          cancelLabel: "Quitar de la lista",
          disableBackdropClose: true,
          wide: esProductoNoElaboradoConDetalle,
          message: (
            <SoftBox display="flex" flexDirection="column" gap={1}>
              <SoftTypography variant="button" fontWeight="regular">
                {esProductoNoElaboradoConDetalle
                  ? "Estos son los ingredientes que no alcanzan para preparar el pedido."
                  : "No hay stock suficiente para sincronizar este producto."}
              </SoftTypography>
              <SoftTypography variant="button" fontWeight="bold" color="dark">
                Producto: {detalleActual?.producto?.nombre || `Materia prima ${stockConflict?.idMateriaPrima}`}
              </SoftTypography>
              {esProductoNoElaboradoConDetalle ? (
                <IngredientesFaltantesTable ingredientes={ingredientesFaltantes} />
              ) : (
                <>
                  <SoftTypography variant="button" color="dark">
                    Disponible: {stockConflict?.disponibleRaw}
                  </SoftTypography>
                  <SoftTypography variant="button" color="dark">
                    Requerido: {stockConflict?.requeridoRaw}
                  </SoftTypography>
                </>
              )}
              <SoftTypography variant="caption" color="text">
                {esProductoNoElaboradoConDetalle
                  ? cantidadAjustada > 0
                    ? `Con el stock actual se pueden preparar ${cantidadAjustada} unidad(es). Puede ajustar el pedido o quitar el producto.`
                    : "No se puede preparar ninguna unidad con el stock actual. El producto se quitara del pedido."
                  : "Si continua, la cantidad se ajustara a lo disponible. Si no, el producto se quitara de la lista."}
              </SoftTypography>
            </SoftBox>
          ),
          func: () =>
            handleAfterDecision((detalleVigente) => {
              if (cantidadAjustada > 0) {
                updateDetalleQuantityLocally(detalleVigente, cantidadAjustada);
                return;
              }
              removeDetalleLocally(detalleVigente, null);
            }),
          cancelFunc: () =>
            handleAfterDecision((detalleVigente) => {
              removeDetalleLocally(detalleVigente, null);
            }),
        });
      });
    },
    [confirmar, removeDetalleLocally, updateDetalleQuantityLocally]
  );

  useEffect(() => {
    resolveStockConflictHandlerRef.current = resolveStockConflict;
  }, [resolveStockConflict]);

  const ejecutarAccion = useCallback(
    async (accion, item, motivo) => {
      switch (accion) {
        case "borrar": {
          removeDetalleLocally(item, motivo || null);
          break;
        }
        case "listo": {
          const detalleIdentity = getDetalleIdentity(item);
          const detalleActual = findDetalleByIdentity(pedidoRef.current?.detalles || [], detalleIdentity);
          if (!detalleActual || detalleActual?.estado === "ENTREGADO") return;

          const detalleActualizado = {
            ...detalleActual,
            estado: "ENTREGADO",
          };

          setPedido((prevPedido) => ({
            ...prevPedido,
            detalles: (prevPedido?.detalles || []).map((detalle) =>
              getDetalleIdentity(detalle) === detalleIdentity ? detalleActualizado : detalle
            ),
          }));

          enqueueDetalleSync(detalleActualizado, {
            localIdentity: detalleIdentity,
            idProducto: Number(detalleActualizado?.producto?.id || detalleActualizado?.idProducto || 0),
          });
          scheduleDraftOutboxProcessing();
          break;
        }
        case "pendientesListos": {
          const detallesPendientes = (pedidoRef.current?.detalles || []).filter(
            (detalle) => detalle?.estado === "PENDIENTE"
          );
          if (!detallesPendientes.length) return;

          const pendientesByIdentity = new Map(
            detallesPendientes.map((detalle) => [getDetalleIdentity(detalle), detalle])
          );

          setPedido((prevPedido) => ({
            ...prevPedido,
            detalles: (prevPedido?.detalles || []).map((detalle) => {
              const detalleIdentity = getDetalleIdentity(detalle);
              if (!pendientesByIdentity.has(detalleIdentity)) {
                return detalle;
              }

              return {
                ...detalle,
                estado: "ENTREGADO",
              };
            }),
          }));

          detallesPendientes.forEach((detallePendiente) => {
            const detalleIdentity = getDetalleIdentity(detallePendiente);
            enqueueDetalleSync(
              {
                ...detallePendiente,
                estado: "ENTREGADO",
              },
              {
                localIdentity: detalleIdentity,
                idProducto: Number(detallePendiente?.producto?.id || detallePendiente?.idProducto || 0),
              }
            );
          });
          scheduleDraftOutboxProcessing();
          break;
        }
        case "quitarPendientes": {
          const detallesPendientes = (pedidoRef.current?.detalles || []).filter(
            (detalle) => detalle.estado === "PENDIENTE"
          );

          if (!detallesPendientes.length) return;

          const detallesPersistidos = detallesPendientes.filter((detalle) => !!detalle?.id);
          const detallesLocales = detallesPendientes.filter((detalle) => !detalle?.id);
          const detallesLocalesIds = new Set(detallesLocales.map((detalle) => getDetalleIdentity(detalle)));

          setPedido((prevPedido) => ({
            ...prevPedido,
            detalles: (prevPedido?.detalles || [])
              .map((detalle) =>
                detalle?.estado === "PENDIENTE" && detalle?.id
                  ? {
                      ...detalle,
                      estado: "CANCELADO",
                      motivoCancelacion: null,
                    }
                  : detalle
              )
              .filter((detalle) => !detallesLocalesIds.has(getDetalleIdentity(detalle))),
          }));

          detallesPersistidos.forEach((detallePendiente) => {
            enqueueDetalleSync(
              {
                ...detallePendiente,
                estado: "CANCELADO",
                motivoCancelacion: null,
              },
              { localIdentity: getDetalleIdentity(detallePendiente) }
            );
          });

          if (detallesPersistidos.length > 0) {
            scheduleDraftOutboxProcessing();
          }

          if (detallesLocales.length > 0) {
            setDraftOutbox((prevItems) =>
              prevItems.filter((outboxItem) => !detallesLocalesIds.has(outboxItem?.localIdentity))
            );
          }
          break;
        }
        default:
          break;
      }
    },
    [enqueueDetalleSync, removeDetalleLocally, scheduleDraftOutboxProcessing]
  );

  const handleDetalleCantidadChange = useCallback(
    async (item, delta) => {
      if (delta === 0) return;

      const detallesActuales = pedidoRef.current?.detalles || [];
      const index = detallesActuales.findIndex((detalle) => getDetalleIdentity(detalle) === getDetalleIdentity(item));
      if (index === -1) return;

      const detalleActual = detallesActuales[index];
      if (detalleActual?.estado !== "PENDIENTE") return;

      const nuevaCantidad = Number(detalleActual?.cantidad || 0) + delta;

      if (nuevaCantidad <= 0) {
        removeDetalleLocally(detalleActual, null);
        return;
      }

      const stockDisponible = Number(detalleActual?.producto?.stockCantidad ?? detalleActual?.stockCantidad ?? 0);

      if (delta > 0 && stockDisponible > 0 && nuevaCantidad > stockDisponible) {
        handleSnackbar("Stock agotado para este producto.", "error");
        return;
      }

      updateDetalleQuantityLocally(detalleActual, nuevaCantidad);
    },
    [handleSnackbar, removeDetalleLocally, updateDetalleQuantityLocally]
  );

  const handleSave = useCallback(
    async (paraFacturar = false) => {
      setIsFinalActionPending(true);

      try {
        await flushDraftOutbox({ showError: true });
      } catch (error) {
        handleSnackbar(`Error al sincronizar cambios pendientes: ${error?.message || error}`, "error");
        setIsFinalActionPending(false);
        return;
      }

      const pedidoActual = pedidoRef.current;
      if (pedidoActual?.paraLlevar === undefined) {
        setIsFinalActionPending(false);
        return;
      }

      if (
        paraFacturar &&
        (pedidoActual?.detalles || []).every((detalle) => detalle.estado === "CANCELADO") &&
        pedidoActual?.id
      ) {
        runSavePedido({
          variables: {
            input: {
              id: pedidoActual?.id,
              idMesa: mesaElegida?.id,
              idMozo: pedidoActual.mozo,
              total: null,
              paraFacturar: false,
              estado: "ANULADO",
              paraLlevar: pedidoActual?.paraLlevar === 1 || !!pedidoActual?.paraLlevar,
              comentario: pedidoActual?.comentario || "",
              detalles: [],
            },
          },
        }).finally(() => {
          setIsFinalActionPending(false);
          refetch?.();
          clearDraftSession();
          handleClose();
        });
        return;
      }

      runSavePedido({
        variables: {
          input: buildPedidoInput({
            pedidoState: pedidoActual,
            mesaId: mesaElegida?.id,
            idSede: Number(pedidoActual?.idSede || user?.sede?.id || 1),
            paraFacturar,
          }),
        },
      })
        .then(async ({ data }) => {
          const pedidoGuardado = data?.savePedido;

          if (!paraFacturar) {
            const detallesTransformados = (pedidoGuardado?.detalles || []).map(({ numeroComanda, ...resto }) => ({
              nroComanda: numeroComanda,
              ...resto,
            }));

            const productosNoImpresos = detallesTransformados.filter(detalleRequiereImpresionComanda);

            if (productosNoImpresos.length > 0) {
              const seccionesInvolucradas = obtenerSeccionesInvolucradas(productosNoImpresos);
              const comentarioPedidoBase = pedidoGuardado?.comentario || "";
              const comentarioPedido = pedidoGuardado?.paraLlevar
                ? comentarioPedidoBase
                  ? `${comentarioPedidoBase} - ESTE PEDIDO ES PARA LLEVAR`
                  : "ESTE PEDIDO ES PARA LLEVAR"
                : comentarioPedidoBase;
              const mozoInfo = mozoData ? `${mozoData?.nombre} ${mozoData?.apellido}` : "";
              const clienteInfo = mesaElegida?.cliente
                ? `${mesaElegida?.cliente?.nombre} ${mesaElegida?.cliente?.apellido}`
                : null;

              const printPromises = seccionesInvolucradas.map((seccion) => {
                const productosDeSeccion = productosNoImpresos.filter(
                  (detalle) => detalleEsImprimible(detalle) && detallePerteneceASeccion(detalle, seccion)
                );

                return {
                  promise: printComanda(
                    productosDeSeccion,
                    seccion,
                    comentarioPedido,
                    mesaElegida?.numero,
                    mozoInfo,
                    clienteInfo
                  ),
                  seccion,
                  detalles: productosDeSeccion,
                };
              });

              const printResults = await Promise.allSettled(printPromises.map((item) => item.promise));
              const impresionesFallidas = [];
              let idsDetallesImpresos = [];

              printResults.forEach((result, index) => {
                const { seccion, detalles } = printPromises[index];

                if (result.status === "fulfilled" && result?.value?.status === 200) {
                  idsDetallesImpresos.push(...detalles.map((detalle) => detalle.id));
                } else {
                  const error =
                    result.status === "fulfilled"
                      ? result.value?.data?.message || "Error de impresion"
                      : result.reason?.message || "Error desconocido";
                  impresionesFallidas.push({ seccion, error, detalles });
                }
              });

              if (idsDetallesImpresos.length > 0) {
                const idsUnicos = [...new Set(idsDetallesImpresos)];

                try {
                  await marcarDetallesComoImpresos({
                    variables: {
                      idsDetalles: idsUnicos,
                    },
                  });
                } catch (error) {
                  console.error("Error al marcar detalles como impresos:", error);
                }
              }

              if (impresionesFallidas.length === 0) {
                handleSnackbar("Mesa guardada correctamente", "success");
              } else {
                const resumenFallas = resumirImpresionesFallidasPorComandera(impresionesFallidas);
                const detalleFallas = formatearResumenImpresionesFallidas(resumenFallas);
                const sufijoFallas = detalleFallas || impresionesFallidas.map((falla) => falla.seccion).join(", ");
                const prefijo =
                  resumenFallas.length === 1
                    ? "Mesa guardada, pero fallo la impresion"
                    : "Mesa guardada, pero fallaron impresiones";

                handleSnackbar(`${prefijo}: ${sufijoFallas}`, "warning");
              }
            } else {
              handleSnackbar("Mesa guardada correctamente", "success");
            }

            clearDraftSession();
            handleClose();
          } else {
            const pedidoServidor = hydratePedidoDesdeServidor(pedidoGuardado, pedidoActual);
            setPedidoPersistido(pedidoServidor);
            setPedido(pedidoServidor);
            setID(pedidoGuardado?.id);
            clearDraftSession();
            setModalFacturar(true);
          }
        })
        .catch((error) => {
          handleSnackbar(`Error al guardar mesa: ${error?.message || error}`, "error");
        })
        .finally(() => {
          setIsFinalActionPending(false);
          refetch?.();
        });
    },
    [
      handleClose,
      handleSnackbar,
      hydratePedidoDesdeServidor,
      clearDraftSession,
      flushDraftOutbox,
      marcarDetallesComoImpresos,
      mesaElegida?.cliente,
      mesaElegida?.id,
      mesaElegida?.numero,
      mozoData,
      refetch,
      runSavePedido,
      user?.sede?.id,
    ]
  );

  const handleFinishAndCloseMesa = useCallback(() => {
    const detallesNoCancelados = (pedido?.detalles || []).filter((detalle) => detalle.estado !== "CANCELADO");
    const tienePendientesDeEntrega = detallesNoCancelados.some((detalle) => detalle.estado !== "ENTREGADO");

    if (tienePendientesDeEntrega) {
      confirmar({
        title: "Productos pendientes de entrega",
        message:
          "Existen productos pendientes de entrega. Si confirma estos se marcaran como entregados. ¿Desea continuar?",
        icon: "warning",
        func: () => {
          handleSave(true);
        },
      });
      return;
    }

    handleSave(true);
  }, [confirmar, handleSave, pedido?.detalles]);

  useEffect(() => {
    let totalCalculado = 0;
    (pedido?.detalles || []).forEach((detalle) => {
      if (detalle.estado !== "CANCELADO") {
        totalCalculado += Number(detalle.precio) * Number(detalle.cantidad);
      }
    });
    setTotal(totalCalculado);
  }, [pedido?.detalles]);

  const mozoLabel = mozoData ? `${mozoData?.nombre} ${mozoData?.apellido}` : "Seleccionar mozo";
  const tienePendientes = detallesActivos.some((detalle) => detalle?.estado === "PENDIENTE");
  const hasInvalidPrices = (pedido?.detalles || []).some(
    (detalle) => detalle?.precio === 0 && detalle?.estado !== "CANCELADO"
  );
  const modalDisabled = isPedidoReadOnly || loadingSaveEffective;
  const canFinalizeAndClose = detallesActivos.length > 0 && !!pedido?.mozo && !modalDisabled && !hasInvalidPrices;
  const effectiveStockRefreshToken = `${stockRefreshToken}:${liveStockRefreshTick}`;
  const emptyPendingProducts = useMemo(() => ({}), []);
  const emptyPendingDetalles = useMemo(() => ({}), []);
  const modalPedidoContextValue = useMemo(
    () => ({
      pedido,
      setPedido,
      pedidoPersistido,
      total,
      disabled: modalDisabled,
      pendingProductIds: emptyPendingProducts,
      pendingDetalleIds: emptyPendingDetalles,
      draftSyncStatus,
      ejecutarAccion,
      onCantidadChange: handleDetalleCantidadChange,
      onSincronizarDetalleMesa: sincronizarDetalleMesa,
    }),
    [
      draftSyncStatus,
      emptyPendingDetalles,
      emptyPendingProducts,
      ejecutarAccion,
      handleDetalleCantidadChange,
      modalDisabled,
      pedido,
      pedidoPersistido,
      sincronizarDetalleMesa,
      total,
    ]
  );

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      closeAfterTransition
      disableEscapeKeyDown={false}
      onClose={handleCloseWithValidationEffective}
    >
      <ModalPedidoProvider value={modalPedidoContextValue}>
        <Fade in={open}>
          <Card sx={style}>
            <SoftBox display="flex" flexDirection="column" gap={2.5}>
              <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <SoftBox flex={1} minWidth={0}>
                  <SoftTypography variant="h6" fontWeight="bold">
                    Mesa {mesaElegida?.numero}
                  </SoftTypography>
                </SoftBox>

                <SoftBox flex={1} display="flex" justifyContent="center" minWidth={0}>
                  {user?.tipoUser === "Mozo" ? (
                    <SoftTypography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      Mozo: {mozoLabel}
                    </SoftTypography>
                  ) : (
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      gap={0.5}
                      onClick={handleOpenMozosMenu}
                      sx={{
                        cursor: modalDisabled ? "default" : "pointer",
                        maxWidth: "100%",
                      }}
                    >
                      <SoftTypography
                        variant="h6"
                        fontWeight="bold"
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        Mozo: {mozoLabel}
                      </SoftTypography>
                      {!modalDisabled ? <KeyboardArrowDown fontSize="small" /> : null}
                    </SoftBox>
                  )}
                </SoftBox>

                <SoftBox flex={1} display="flex" justifyContent="flex-end">
                  <Tooltip title="Cerrar" placement="top">
                    <IconButton onClick={handleCloseWithValidationEffective}>
                      <Close />
                    </IconButton>
                  </Tooltip>
                </SoftBox>
              </SoftBox>

              {draftSyncStatus?.pendingCount > 0 || draftSyncStatus?.lastError ? (
                <Card
                  sx={{
                    borderRadius: 3,
                    border: "1px solid rgba(255, 204, 57, 0.28)",
                    boxShadow: "none",
                    backgroundColor: "#fff9e4",
                  }}
                >
                  <SoftBox px={1.5} py={1}>
                    <SoftTypography variant="caption" fontWeight="bold" color="dark">
                      {draftSyncStatus?.lastError
                        ? `Modo local activo. Hay ${draftSyncStatus?.pendingCount} cambio(s) pendientes de sincronizar.`
                        : draftSyncStatus?.isSyncing
                        ? `Sincronizando ${draftSyncStatus?.pendingCount} cambio(s) en segundo plano...`
                        : `Modo local activo. Hay ${draftSyncStatus?.pendingCount} cambio(s) pendientes.`}
                    </SoftTypography>
                  </SoftBox>
                </Card>
              ) : null}

              <AgregarProducto stockRefreshToken={effectiveStockRefreshToken} />

              <SoftBox display="flex" justifyContent="space-between" alignItems="center" gap={1.5}>
                <Tooltip title="Marcar pendientes como entregados" placement="top">
                  <span>
                    <SoftButton
                      size="small"
                      circular
                      onClick={() => ejecutarAccion("pendientesListos")}
                      disabled={!tienePendientes || modalDisabled || hasInvalidPrices}
                      sx={{
                        px: 2.25,
                        py: 1,
                        borderRadius: 999,
                        backgroundColor: "#ec008c",
                        color: "#fff",
                        boxShadow: "0 10px 20px rgba(236, 0, 140, 0.2)",
                        "&:hover": {
                          backgroundColor: "#d90080",
                        },
                        "&.Mui-disabled": {
                          color: "rgba(255, 255, 255, 0.72)",
                        },
                      }}
                    >
                      TODO ENTREGADO&nbsp;
                      <Icon sx={{ fontWeight: "bold", fontSize: 18, color: "inherit" }}>done</Icon>
                    </SoftButton>
                  </span>
                </Tooltip>

                <SoftButton
                  size="small"
                  circular
                  onClick={() => ejecutarAccion("quitarPendientes")}
                  disabled={!tienePendientes || modalDisabled}
                  sx={{
                    px: 2.25,
                    py: 1,
                    borderRadius: 999,
                    backgroundColor: "#8392ab",
                    color: "#fff",
                    boxShadow: "0 8px 18px rgba(52, 71, 103, 0.14)",
                    "&:hover": {
                      backgroundColor: "#6d7890",
                    },
                    "&.Mui-disabled": {
                      color: "rgba(255, 255, 255, 0.72)",
                    },
                  }}
                >
                  Quitar todo&nbsp;
                  <Icon sx={{ fontSize: 18 }}>delete</Icon>
                </SoftButton>
              </SoftBox>

              <ListadoDetalles />

              <SoftBox>
                <InputLabel htmlFor="comentario-pedido">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Comentario adicional
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="comentario-pedido"
                  placeholder="Comentario adicional"
                  name="comentario"
                  value={pedido?.comentario || ""}
                  onChange={handleChangePedido}
                  disabled={modalDisabled}
                  fullWidth
                />
              </SoftBox>

              <SoftButton
                color="success"
                circular
                fullWidth
                onClick={handleFinishAndCloseMesa}
                disabled={!canFinalizeAndClose}
              >
                {loadingSaveEffective ? (
                  <CircularProgress size={15} color="inherit" />
                ) : (
                  <>
                    <Icon sx={{ fontWeight: "light" }}>done_all</Icon>
                    &nbsp;Finalizar y cerrar mesa
                  </>
                )}
              </SoftButton>

              <SoftButton
                color="uproGreen"
                circular
                fullWidth
                onClick={() => handleSave(false)}
                disabled={!hasRealChangesCurrent || modalDisabled || hasInvalidPrices || detallesActivos.length === 0}
              >
                {loadingSaveEffective ? <CircularProgress size={15} color="inherit" /> : "Guardar"}
              </SoftButton>

              <ModalFacturar
                open={modalFacturar}
                handleClose={(event, reason) => {
                  if (reason !== "backdropClick") {
                    setModalFacturar(false);
                  }
                }}
                total={total}
                idPedido={pedido?.id || id}
                pedido={pedido}
                cerrarVenta={() => {
                  refetch?.();
                  handleClose();
                }}
                setPedido={setPedido}
                mesa={mesaElegida?.numero}
              />
            </SoftBox>
          </Card>
        </Fade>
      </ModalPedidoProvider>
    </Modal>
  );
}

ModalPedido.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  mesaElegida: PropTypes.object,
  refetch: PropTypes.func,
  savePedido: PropTypes.func,
  loadingSave: PropTypes.bool,
  stockRefreshToken: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};
